/**
 * ProjectAnalyticsService
 *
 * Service for managing project-level analytics integrations in Firestore.
 * Stores settings in: projects/{projectId}/settings/analytics
 *
 * Features:
 * - CRUD operations for analytics integrations
 * - Consent management configuration
 * - Real-time subscription support
 *
 * @module analytics-settings/services
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type {
  ProjectAnalyticsSettings,
  AnalyticsIntegration,
  ConsentManagementConfig,
} from '../types/analytics.types';
import { DEFAULT_ANALYTICS_SETTINGS } from '../types/analytics.types';

/**
 * Response from Facebook CAPI test event endpoint
 */
export interface FacebookCAPITestResult {
  success: boolean;
  eventsReceived?: number;
  message?: string;
  error?: string;
}

/**
 * Parameters for sending Facebook CAPI test event
 */
export interface FacebookCAPITestParams {
  projectId: string;
  pixelId: string;
  accessToken: string;
  testEventCode: string;
  eventName?: string;
}

/**
 * Response from TikTok Events API test event endpoint
 */
export interface TikTokEventsAPITestResult {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Parameters for sending TikTok Events API test event
 */
export interface TikTokEventsAPITestParams {
  projectId: string;
  pixelCode: string;
  accessToken: string;
  testEventCode?: string;
  eventName?: string;
}

/**
 * Get builder service API URL
 * Uses runtime hostname detection for proper URL in all environments
 */
function getApiUrl(): string {
  // First check env var (for backwards compatibility)
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }

  // Runtime detection based on hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Local development → Builder Service on 8081
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }

  // Dev environment
  if (hostname.includes('devsegmentlyai')) {
    return 'https://builder-service-519070724517.us-central1.run.app';
  }

  // Production
  return 'https://api.segmently.ai';
}

/**
 * ProjectAnalyticsService
 *
 * Manages analytics settings for a specific project.
 * Settings are stored in Firestore at: projects/{projectId}/settings/analytics
 */
export class ProjectAnalyticsService {
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  /**
   * Get the Firestore document reference for project analytics settings
   */
  private getDocRef(projectId: string) {
    return doc(this.firestore, 'projects', projectId, 'settings', 'analytics');
  }

  /**
   * Get analytics settings for a project
   *
   * @param projectId - The project ID
   * @returns Project analytics settings or defaults if not configured
   */
  async getSettings(projectId: string): Promise<ProjectAnalyticsSettings> {
    try {
      const docRef = this.getDocRef(projectId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return { ...DEFAULT_ANALYTICS_SETTINGS };
      }

      const data = docSnap.data() as ProjectAnalyticsSettings;
      return {
        consentManagement: data.consentManagement ?? DEFAULT_ANALYTICS_SETTINGS.consentManagement,
        integrations: data.integrations ?? [],
      };
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectAnalyticsService.getSettings');
      throw error;
    }
  }

  /**
   * Save complete analytics settings for a project
   *
   * @param projectId - The project ID
   * @param settings - Complete analytics settings
   */
  async saveSettings(projectId: string, settings: ProjectAnalyticsSettings): Promise<void> {
    try {
      const docRef = this.getDocRef(projectId);
      await setDoc(docRef, settings, { merge: false });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectAnalyticsService.saveSettings');
      throw error;
    }
  }

  /**
   * Update consent management configuration
   *
   * @param projectId - The project ID
   * @param config - Consent management configuration
   */
  async updateConsentManagement(
    projectId: string,
    config: ConsentManagementConfig
  ): Promise<void> {
    try {
      const docRef = this.getDocRef(projectId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        // Create new document with default integrations
        await setDoc(docRef, {
          consentManagement: config,
          integrations: [],
        });
      } else {
        await updateDoc(docRef, { consentManagement: config });
      }
    } catch (error) {
      CoreFirebaseService.getInstance().logError(
        error,
        'ProjectAnalyticsService.updateConsentManagement'
      );
      throw error;
    }
  }

  /**
   * Add a new analytics integration
   *
   * @param projectId - The project ID
   * @param integration - The integration to add (without ID)
   * @returns The created integration with generated ID
   */
  async addIntegration(
    projectId: string,
    integration: Omit<AnalyticsIntegration, 'id'>
  ): Promise<AnalyticsIntegration> {
    try {
      const settings = await this.getSettings(projectId);
      const newIntegration: AnalyticsIntegration = {
        ...integration,
        id: this.generateId(),
      };

      settings.integrations.push(newIntegration);
      await this.saveSettings(projectId, settings);

      return newIntegration;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectAnalyticsService.addIntegration');
      throw error;
    }
  }

  /**
   * Update an existing integration
   *
   * @param projectId - The project ID
   * @param integrationId - The integration ID to update
   * @param updates - Partial updates to apply
   */
  async updateIntegration(
    projectId: string,
    integrationId: string,
    updates: Partial<Omit<AnalyticsIntegration, 'id' | 'createdAt'>>
  ): Promise<void> {
    try {
      const settings = await this.getSettings(projectId);
      const index = settings.integrations.findIndex((i) => i.id === integrationId);

      if (index === -1) {
        throw new Error(`Integration not found: ${integrationId}`);
      }

      settings.integrations[index] = {
        ...settings.integrations[index],
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      await this.saveSettings(projectId, settings);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(
        error,
        'ProjectAnalyticsService.updateIntegration'
      );
      throw error;
    }
  }

  /**
   * Delete an integration
   *
   * @param projectId - The project ID
   * @param integrationId - The integration ID to delete
   */
  async deleteIntegration(projectId: string, integrationId: string): Promise<void> {
    try {
      const settings = await this.getSettings(projectId);
      settings.integrations = settings.integrations.filter((i) => i.id !== integrationId);
      await this.saveSettings(projectId, settings);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(
        error,
        'ProjectAnalyticsService.deleteIntegration'
      );
      throw error;
    }
  }

  /**
   * Toggle integration enabled/disabled
   *
   * @param projectId - The project ID
   * @param integrationId - The integration ID to toggle
   * @param enabled - New enabled state
   */
  async toggleIntegration(
    projectId: string,
    integrationId: string,
    enabled: boolean
  ): Promise<void> {
    await this.updateIntegration(projectId, integrationId, { enabled });
  }

  /**
   * Get only enabled integrations (for publish flow)
   *
   * @param projectId - The project ID
   * @returns Array of enabled integrations
   */
  async getEnabledIntegrations(projectId: string): Promise<AnalyticsIntegration[]> {
    const settings = await this.getSettings(projectId);
    return settings.integrations.filter((i) => i.enabled);
  }

  /**
   * Subscribe to real-time settings updates
   *
   * @param projectId - The project ID
   * @param callback - Called when settings change
   * @returns Unsubscribe function
   */
  subscribeToSettings(
    projectId: string,
    callback: (settings: ProjectAnalyticsSettings) => void
  ): Unsubscribe {
    const docRef = this.getDocRef(projectId);

    return onSnapshot(
      docRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          callback({ ...DEFAULT_ANALYTICS_SETTINGS });
          return;
        }

        const data = docSnap.data() as ProjectAnalyticsSettings;
        callback({
          consentManagement: data.consentManagement ?? DEFAULT_ANALYTICS_SETTINGS.consentManagement,
          integrations: data.integrations ?? [],
        });
      },
      (error) => {
        CoreFirebaseService.getInstance().logError(
          error,
          'ProjectAnalyticsService.subscribeToSettings'
        );
      }
    );
  }

  // ============================================
  // API Methods (Builder Service calls)
  // ============================================

  /**
   * Get authorization headers for authenticated requests
   * @throws Error if user is not authenticated
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Send a test event to Facebook Conversion API
   *
   * This calls the Builder Service endpoint which sends directly to Facebook CAPI.
   * The test event uses the testEventCode so it appears in Meta Events Manager → Test Events.
   *
   * @param params - Test event parameters
   * @returns Test result with success/error status
   */
  async sendFacebookCAPITestEvent(params: FacebookCAPITestParams): Promise<FacebookCAPITestResult> {
    try {
      const headers = await this.getAuthHeaders();
      const apiUrl = getApiUrl();

      const response = await fetch(`${apiUrl}/api/analytics/facebook-capi/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          projectId: params.projectId,
          pixelId: params.pixelId,
          accessToken: params.accessToken,
          testEventCode: params.testEventCode,
          eventName: params.eventName || 'Test Segmently Integration',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP error ${response.status}`,
        };
      }

      return data as FacebookCAPITestResult;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(
        error,
        'ProjectAnalyticsService.sendFacebookCAPITestEvent'
      );
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Send a test event to TikTok Events API
   *
   * This calls the Builder Service endpoint which sends directly to TikTok Events API.
   * The test event uses the testEventCode so it appears in TikTok Events Manager → Test Events.
   *
   * @param params - Test event parameters
   * @returns Test result with success/error status
   */
  async sendTikTokEventsAPITestEvent(params: TikTokEventsAPITestParams): Promise<TikTokEventsAPITestResult> {
    try {
      const headers = await this.getAuthHeaders();
      const apiUrl = getApiUrl();

      const response = await fetch(`${apiUrl}/api/analytics/tiktok-events-api/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          projectId: params.projectId,
          pixelCode: params.pixelCode,
          accessToken: params.accessToken,
          testEventCode: params.testEventCode,
          eventName: params.eventName || 'Test Segmently Integration',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP error ${response.status}`,
        };
      }

      return data as TikTokEventsAPITestResult;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(
        error,
        'ProjectAnalyticsService.sendTikTokEventsAPITestEvent'
      );
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // ============================================
  // Private Helpers
  // ============================================

  /**
   * Generate a unique ID for integrations
   */
  private generateId(): string {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 8);
    return `int_${timestamp}_${randomPart}`;
  }
}

// Singleton instance for convenience
let serviceInstance: ProjectAnalyticsService | null = null;

export function getProjectAnalyticsService(): ProjectAnalyticsService {
  if (!serviceInstance) {
    serviceInstance = new ProjectAnalyticsService();
  }
  return serviceInstance;
}
